import { useEffect } from 'react';
import { REGION_LIST } from '@constants/limits';
import { query, type OffsetPage } from './paging';
import { useApi } from './useApi';

/** A Kemendagri province ("32", "Jawa Barat"). */
export interface Province { code: string; name: string }
export type RegencyKind = 'REGENCY' | 'CITY';
/** A regency or city ("32.10", "Kabupaten Majalengka"). */
export interface Regency { code: string; provinceCode: string; name: string; kind: RegencyKind }
/** A district (kecamatan, "31.74.04"). */
export interface District { code: string; regencyCode: string; name: string }
/** A kelurahan (URBAN_VILLAGE), a desa (VILLAGE) or a desa adat (CUSTOMARY_VILLAGE), from its Kemendagri code. */
export type VillageKind = 'URBAN_VILLAGE' | 'VILLAGE' | 'CUSTOMARY_VILLAGE';
/** A village (desa / kelurahan, "31.74.04.1004"). */
export interface Village { code: string; districtCode: string; name: string; kind: VillageKind }
/** GET /regions/villages/:code: the village with the names of its district, regency and province. */
export interface VillageDetail extends Village {
  districtName: string;
  regencyCode: string;
  regencyName: string;
  provinceCode: string;
  provinceName: string;
}
export type DistrictPage = OffsetPage<District>;
export type VillagePage = OffsetPage<Village>;

/** `q` (part of the name) is sent only from REGION_LIST.searchMin characters: the server refuses shorter ones. */
export const regionSearch = (q: string | undefined) => {
  const t = q?.trim() ?? '';
  return t.length >= REGION_LIST.searchMin ? t : undefined;
};

/**
 * Provinces and regencies are small and fixed (38, 514): answered whole. Districts (±7.3 thousand) and villages
 * (±84 thousand) are offset pages, always narrowed by a parent: never all of them. Browsers cache every answer.
 */
export const regionPaths = {
  provinces: '/regions/provinces',
  regencies: (provinceCode: string) => `/regions/regencies${query({ province: provinceCode })}`,
  districts: (f: { regencyCode: string; q?: string; page?: number }) => `/regions/districts${query({ regencyCode: f.regencyCode, q: regionSearch(f.q), page: f.page, limit: REGION_LIST.pageSize })}`,
  villages: (f: { districtCode: string; q?: string; page?: number }) => `/regions/villages${query({ districtCode: f.districtCode, q: regionSearch(f.q), page: f.page, limit: REGION_LIST.pageSize })}`,
  village: (code: string) => `/regions/villages/${encodeURIComponent(code)}`,
};

const PROVINCE_CODE = /^\d{2}$/;
const REGENCY_CODE = /^\d{2}\.\d{2}$/;
const DISTRICT_CODE = /^\d{2}\.\d{2}\.\d{2}$/;
const VILLAGE_CODE = /^\d{2}\.\d{2}\.\d{2}\.[1-3]\d{3}$/;
/** The one check of a province code ("32"), for URL parameters a hand-edited link may have broken. */
export const isProvinceCode = (v: string | null | undefined): v is string => !!v && PROVINCE_CODE.test(v);
/** The one check of a regency/city code ("32.10"). */
export const isRegencyCode = (v: string | null | undefined): v is string => !!v && REGENCY_CODE.test(v);
/** The one check of a district code ("32.10.01"). */
export const isDistrictCode = (v: string | null | undefined): v is string => !!v && DISTRICT_CODE.test(v);
/** The one check of a village code ("32.10.01.2001"). */
export const isVillageCode = (v: string | null | undefined): v is string => !!v && VILLAGE_CODE.test(v);
/** `child` lies inside `parent` (a regency in its province, a district in its regency, ...): codes nest by prefix. */
export const isInside = (child: string, parent: string) => child.startsWith(`${parent}.`);

/** The provinces never change while the app runs: read once, then every screen gets them at once. */
let provincesCache: Province[] | null = null;

/** The 38 provinces (one request per app session, shared by every filter, form and the control room). */
export function useProvinces() {
  const state = useApi<Province[]>(provincesCache ? null : regionPaths.provinces);
  useEffect(() => {
    if (state.data) provincesCache = state.data;
  }, [state.data]);
  return { ...state, data: provincesCache ?? state.data, loading: !provincesCache && state.loading };
}
